/**
 * The API client.
 *
 * One place that knows about tokens, so no screen has to. Refresh is
 * single-flight: a burst of parallel calls after an access token expires
 * must produce one refresh, not six, or the last one back wins and the
 * others are signed out.
 */
import type { Locale } from "../i18n";

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

const ACCESS_KEY = "sangam.access";
const REFRESH_KEY = "sangam.refresh";

export interface Tokens {
  access_token: string;
  refresh_token: string;
  locale: Locale;
}

export interface Me {
  id: number;
  member_no: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  locale: Locale;
  status: "PENDING" | "ACTIVE" | "EXPIRED" | "SUSPENDED";
  valid_until: string | null;
  membership_type: string | null;
  roles: string[];
  capabilities: Record<string, boolean>;
  config: Record<string, number | boolean | string>;
}

/** Club content as the API returns it: the text, plus which language it is
 *  actually in. `fallback_note` is not optional decoration -- when it is set
 *  the screen must show it. */
export interface Localised {
  locale: Locale;
  is_fallback: boolean;
  fallback_note: string | null;
  available_locales: Locale[];
  [field: string]: unknown;
}

export class ApiError extends Error {
  /**
   * A stable code from the server's booking errors (`seat_taken`,
   * `hold_expired`, `members_only`, …), when it sent one.
   *
   * The client translates the code; `message` is the server's English and is
   * for logs, never for a member's screen — that would put one English
   * sentence on an otherwise Tamil page.
   */
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Thrown when the device is offline. Screens that have cached data (the QR
 *  ticket, the scanner's guest list) catch this and carry on. */
export class OfflineError extends Error {
  constructor() {
    super("offline");
    this.name = "OfflineError";
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode; the session simply does not survive a reload */
  }
}

export function storeTokens(tokens: Tokens): void {
  write(ACCESS_KEY, tokens.access_token);
  write(REFRESH_KEY, tokens.refresh_token);
}

export function clearTokens(): void {
  write(ACCESS_KEY, null);
  write(REFRESH_KEY, null);
}

export function isSignedIn(): boolean {
  return read(ACCESS_KEY) !== null;
}

// Single-flight refresh. Every 401 during a refresh awaits this same promise.
let refreshing: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const token = read(REFRESH_KEY);
  if (!token) return false;

  const response = await fetch(`${BASE}/api/v1/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: token }),
  });
  if (!response.ok) {
    clearTokens();
    return false;
  }
  storeTokens((await response.json()) as Tokens);
  return true;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  locale?: Locale;
  /** Set false for calls a signed-out visitor may also make. */
  auth?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, locale, auth = true } = options;

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    // Only consulted by the server before a member has chosen; after that
    // the profile wins. Sent anyway so a guest browsing the programme list
    // gets club content in the language they picked on the welcome screen.
    if (locale) headers["X-Sangam-Locale"] = locale;
    const token = auth ? read(ACCESS_KEY) : null;
    if (token) headers.Authorization = `Bearer ${token}`;

    try {
      return await fetch(`${BASE}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      // fetch only rejects on a network failure, never on an HTTP error.
      throw new OfflineError();
    }
  };

  let response = await send();

  if (response.status === 401 && auth && read(REFRESH_KEY)) {
    refreshing ??= refreshTokens().finally(() => {
      refreshing = null;
    });
    if (await refreshing) response = await send();
  }

  if (!response.ok) {
    let detail = response.statusText;
    let code: string | null = null;
    try {
      // FastAPI's `detail` is a plain string for most errors, and the
      // {code, message} object the booking routes raise.
      const parsed = (await response.json()) as {
        detail?: string | { code?: string; message?: string };
      };
      if (typeof parsed.detail === "string") {
        detail = parsed.detail;
      } else if (parsed.detail && typeof parsed.detail === "object") {
        code = parsed.detail.code ?? null;
        detail = parsed.detail.message ?? detail;
      }
    } catch {
      /* an error body that is not JSON is still an error */
    }
    throw new ApiError(response.status, detail, code);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  register: (body: {
    name: string;
    email: string;
    password: string;
    phone?: string;
    locale?: Locale;
  }) => request<Tokens>("/api/v1/auth/register", { method: "POST", body, auth: false }),

  login: (body: { email: string; password: string }) =>
    request<Tokens>("/api/v1/auth/login", { method: "POST", body, auth: false }),

  logout: () => request<void>("/api/v1/auth/logout", { method: "POST" }),

  me: () => request<Me>("/api/v1/me"),

  updateMe: (body: Partial<Pick<Me, "name" | "phone" | "locale">> & { address?: string }) =>
    request<Me>("/api/v1/me", { method: "PATCH", body }),

  meta: (locale?: Locale) => request<MetaResponse>("/api/v1/meta", { auth: false, locale }),

  programs: (params: { q?: string; category?: string } = {}, locale?: Locale) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    if (params.category) search.set("category", params.category);
    const qs = search.toString();
    return request<{ locale: Locale; programs: ProgramSummary[] }>(
      `/api/v1/programs${qs ? `?${qs}` : ""}`,
      { auth: false, locale },
    );
  },

  program: (slug: string, locale?: Locale) =>
    request<ProgramDetail>(`/api/v1/programs/${slug}`, { auth: false, locale }),

  seats: (slug: string) => request<SeatMap>(`/api/v1/programs/${slug}/seats`, { auth: false }),

  hold: (slug: string, body: { seat_ids: number[]; name?: string; phone?: string; email?: string }) =>
    request<BookingOut>(`/api/v1/programs/${slug}/hold`, { method: "POST", body, auth: false }),

  booking: (ref: string) => request<BookingOut>(`/api/v1/bookings/${ref}`, { auth: false }),

  checkout: (ref: string) =>
    request<{ booking: BookingOut; redirect_url: string; provider_ref: string; amount_idr: number }>(
      `/api/v1/bookings/${ref}/checkout`,
      { method: "POST", auth: false },
    ),

  cancelBooking: (ref: string) =>
    request<void>(`/api/v1/bookings/${ref}/cancel`, { method: "POST", auth: false }),

  myBookings: (locale?: Locale) =>
    request<{ upcoming: BookingOut[]; past: BookingOut[] }>("/api/v1/bookings", { locale }),

  // ------------------------------------------------------------------ admin
  // Every one of these is gated by role at the API. The UI hides what a
  // volunteer cannot use as a courtesy; the server is what refuses.
  admin: {
    programs: () => request<{ programs: AdminProgram[] }>("/api/v1/admin/programs"),

    program: (id: number) => request<AdminProgram>(`/api/v1/admin/programs/${id}`),

    createProgram: (body: {
      slug?: string;
      date_time: string;
      venue?: string;
      seating_mode?: "GA" | "NUMBERED";
      category_id?: number | null;
      members_only?: boolean;
      ga_capacity?: number | null;
    }) => request<AdminProgram>("/api/v1/admin/programs", { method: "POST", body }),

    updateProgram: (id: number, body: Record<string, unknown>) =>
      request<AdminProgram>(`/api/v1/admin/programs/${id}`, { method: "PATCH", body }),

    putText: (
      id: number,
      locale: Locale,
      body: { name: string; description?: string; performers?: string },
    ) => request<AdminProgram>(`/api/v1/admin/programs/${id}/texts/${locale}`, {
      method: "PUT",
      body,
    }),

    addTier: (
      id: number,
      body: { label_key: string; price_idr: number; price_non_member_idr?: number | null; sort_order?: number },
    ) => request<AdminProgram>(`/api/v1/admin/programs/${id}/tiers`, { method: "POST", body }),

    deleteTier: (id: number, tierId: number) =>
      request<void>(`/api/v1/admin/programs/${id}/tiers/${tierId}`, { method: "DELETE" }),

    layOutSeats: (id: number, body: { rows: string[]; seats_per_row: number; tier_id: number }) =>
      request<AdminProgram>(`/api/v1/admin/programs/${id}/seats`, { method: "PUT", body }),

    publish: (id: number) =>
      request<AdminProgram>(`/api/v1/admin/programs/${id}/publish`, { method: "POST" }),

    unpublish: (id: number) =>
      request<AdminProgram>(`/api/v1/admin/programs/${id}/unpublish`, { method: "POST" }),

    members: (q = "") =>
      request<{ total: number; members: AdminMember[] }>(
        `/api/v1/admin/members${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      ),

    grantRole: (memberId: number, role: string) =>
      request<AdminMember>(`/api/v1/admin/members/${memberId}/roles`, {
        method: "POST",
        body: { role },
      }),

    revokeRole: (memberId: number, role: string) =>
      request<void>(`/api/v1/admin/members/${memberId}/roles/${role}`, { method: "DELETE" }),

    updateMember: (memberId: number, body: Record<string, unknown>) =>
      request<AdminMember>(`/api/v1/admin/members/${memberId}`, { method: "PATCH", body }),

    settings: () =>
      request<{ settings: AdminSetting[]; unanswered: { key: string }[] }>("/api/v1/admin/settings"),

    putSetting: (key: string, value: string | number | boolean) =>
      request<AdminSetting>(`/api/v1/admin/settings/${key}`, { method: "PUT", body: { value } }),
  },
};

export interface AdminProgram {
  id: number;
  slug: string;
  status: "DRAFT" | "PUBLISHED" | "CANCELLED";
  date_time: string;
  doors_open: string | null;
  venue: string;
  seating_mode: "GA" | "NUMBERED";
  ga_capacity: number | null;
  poster_url: string | null;
  members_only: boolean;
  category_id: number | null;
  published_at: string | null;
  texts: Record<string, { name: string; description: string; performers: string }>;
  /** Locales with no content yet — the editor shows these as empty slots
   *  rather than hiding them, so a gap is visible rather than forgotten. */
  missing_locales: Locale[];
  tiers: {
    id: number;
    label_key: string;
    price_idr: number;
    price_non_member_idr: number | null;
    sort_order: number;
  }[];
  seats: { total: number; available: number; held: number; sold: number };
  bookings: number;
}

export interface AdminMember {
  id: number;
  member_no: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  locale: Locale;
  status: "PENDING" | "ACTIVE" | "EXPIRED" | "SUSPENDED";
  valid_until: string | null;
  roles: string[];
  last_login_at: string | null;
  created_at: string;
}

export interface AdminSetting {
  key: string;
  value: string | number | boolean;
  value_type: "string" | "int" | "bool";
  default: string | number | boolean;
  description: string;
  awaiting_committee: boolean;
  /** Nobody has ever changed it. For a flagged key that means the app is
   *  running on an assumption, and the screen says so. */
  on_default: boolean;
  updated_at: string | null;
  updated_by: number | null;
  editable: boolean;
}

export interface ProgramSummary {
  id: number;
  slug: string;
  date_time: string;
  venue: string;
  seating_mode: "GA" | "NUMBERED";
  poster_url: string | null;
  category: string | null;
  category_label_key: string | null;
  members_only: boolean;
  from_price_idr: number | null;
  sold_out: boolean;
  text: (Localised & { name: string; description: string; performers: string }) | null;
}

export interface ProgramDetail extends ProgramSummary {
  tiers: {
    id: number;
    label_key: string;
    price_idr: number;
    member_price_idr: number;
    non_member_price_idr: number | null;
  }[];
}

export type SeatState = "AVAILABLE" | "HELD" | "SOLD";

export interface SeatMap {
  program_id: number;
  seating_mode: "GA" | "NUMBERED";
  hold_minutes: number;
  rows: {
    row: string;
    seats: { id: number; label: string; number: number; tier_id: number | null; state: SeatState }[];
  }[];
}

export interface BookingOut {
  ref: string;
  state:
    | "HELD"
    | "PENDING_PAYMENT"
    | "PAID"
    | "CHECKED_IN"
    | "CANCELLED"
    | "REFUNDED"
    | "FAILED";
  total_idr: number;
  service_fee_idr: number;
  fee_absorbed: boolean;
  held_until: string | null;
  seats: { label: string; price_idr: number; tier_id: number | null }[];
  program: {
    id: number;
    slug: string;
    date_time: string;
    venue: string;
    text: (Localised & { name: string }) | null;
  };
  qr_payload: string | null;
}

/** A BookingError code from the server, for the client to translate. */
export function errorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

export interface MetaResponse {
  locales: { code: Locale; name: string }[];
  categories: { id: number; code: string; label_key: string }[];
  membership_types: {
    id: number;
    code: string;
    label_key: string;
    months: number;
    fee_idr: number;
  }[];
  venue_timezone: string;
  venue_tz_label: string;
  non_members_may_book: boolean;
  service_fee_idr: number;
  service_fee_absorbed: boolean;
  vocabularies: {
    seat_states: string[];
    booking_states: string[];
    payment_states: string[];
    seating_modes: string[];
    roles: string[];
  };
}
